import type { SalesforceProduct } from "@shared/index";
import type { ApprovalRequestResult, ApprovalStatusResponseRecord, RoasterApprovalRequestDraft } from "@shared/services/salesforce/types";
import type { CreateConnectionParams, FormProduct, Product, ProductConnection, SkuImportDraftState } from "@shared/types";

// Keys used for writing to local storage
export const productLocalStorageKeys = {
  skuImportDraft: "@skuImportDraft",
};

export type UseProductsUtils = {
  handleInstallationId: (installationId: string) => void;
  // Vendors with no Shopify installation (e.g. ShipStation roasters) never call
  // handleInstallationId, so the skuImportDrafts fetch would otherwise stay
  // gated forever. Calling this lets it fire scoped by vendorKey alone —
  // additive to (never a replacement for) the installationId-scoped path.
  handleEnableVendorScopedDrafts: () => void;
  // May return a promise so callers can reflect the in-flight save in their
  // UI (e.g. disabling a Save Draft button until the API responds).
  handlePostSkuImportDraft: (skuImportDraft: FormProduct) => void | Promise<void>;
  // Resolves `false` (never rejects) when the backend update fails or is
  // skipped (e.g. no current vendor) — callers that optimistically clean up
  // local state (e.g. dropping a discarded draft from a batch selection)
  // must gate that cleanup on this, not just on the promise settling.
  handleUpdateSkuImportDrafts: (skuImportDrafts: FormProduct[], status: SkuImportDraftState) => Promise<boolean>;
  handlePostProducts: (serviceId: string, accessToken: string, products: Partial<SalesforceProduct>[]) => Promise<void>;
  handleUpdateSkuStatus: (requests: RoasterApprovalRequestDraft[]) => Promise<ApprovalRequestResult[]>;
  isUpdatingSkuStatus: boolean;
  handleUpdateWeeklyLimit: (request: RoasterApprovalRequestDraft) => Promise<ApprovalRequestResult[]>;
  isUpdatingWeeklyLimit: boolean;
  handleSyncProductConnections?: (connectionParams: CreateConnectionParams[], updatedConnections: ProductConnection[]) => Promise<boolean>;
  // Re-fetches remote products, optionally scoped to a `title` or `productIds`
  // search. Call with no args to restore the full, unfiltered catalog.
  handleSearchRemoteProducts?: (searchParams?: { title?: string; productIds?: string }) => void;
};
export type ProductsContextState = [state: ProductsState, dispatch: ProductsDispatch, utils?: UseProductsUtils];

export type LoadingKey = "remoteProducts" | "localProducts" | "skuImportDrafts" | "serviceProducts" | "connections" | "approvalRequests";

export type LoadingState = {
  [key in LoadingKey]: boolean;
};

export type LoadingKeys = keyof LoadingState;
export type ErrorKey = "remoteProducts" | "localProducts" | "skuImportDrafts" | "serviceProducts" | "connections" | "approvalRequests";
export type ErrorState = {
  [key in ErrorKey]: Error | undefined;
};

export interface ProductsState {
  initialized: boolean;
  loading: LoadingState;
  installationId: string | undefined;
  // Set once for vendors with no Shopify installation (e.g. ShipStation
  // roasters) so the skuImportDrafts fetch can proceed scoped by vendorKey
  // alone. See `handleEnableVendorScopedDrafts` above.
  vendorScopedDraftsEnabled: boolean;
  remoteProducts: Product[] | undefined;
  skuImportDrafts: FormProduct[] | undefined;
  filteredRemoteProducts: Product[] | undefined;
  localProducts: Product[] | undefined;
  filteredLocalProducts: Product[] | undefined;
  serviceProducts: Product[] | undefined;
  connections: ProductConnection[] | undefined;
  filteredConnections: ProductConnection[] | undefined;
  error: ErrorState;
  remoteFormProducts: FormProduct[] | undefined;
  remoteFormProductsWithImported: FormProduct[] | undefined;
  localParentChildProducts: Product[] | undefined;
  approvalRequests: ApprovalStatusResponseRecord[] | undefined;
}

export interface SetSkuImportDraftsAction {
  type: "SET_SKU_IMPORT_DRAFTS";
  payload: {
    skuImportDrafts: FormProduct[];
  };
}

export interface SetRemoteFormProductsWithImportsAction {
  type: "SET_REMOTE_FORM_PRODUCTS_WITH_IMPORTS";
  payload: {
    remoteFormProductsWithImports: FormProduct[];
  };
}
export interface PostSkuImportDraftAction {
  type: "ADD_SKU_IMPORT_DRAFT";
  payload: {
    skuImportDraft: FormProduct;
  };
}

export interface EditSkuImportDraftAction {
  type: "EDIT_SKU_IMPORT_DRAFT";
  payload: {
    skuImportDrafts: FormProduct[];
    status: SkuImportDraftState;
  };
}

export interface SetLocalProductsAction {
  type: "SET_LOCAL_PRODUCTS";
  payload: {
    localProducts: Product[];
  };
}

export interface SetFilteredLocalProductsAction {
  type: "SET_FILTERED_LOCAL_PRODUCTS";
  payload: {
    filteredLocalProducts: Product[];
  };
}
export interface SetRemoteProductsAction {
  type: "SET_REMOTE_PRODUCTS";
  payload: {
    remoteProducts: Product[];
  };
}

export interface SetRemoteFormProductsAction {
  type: "SET_REMOTE_FORM_PRODUCTS";
  payload: {
    remoteFormProducts: FormProduct[];
  };
}

export interface SetRemoteFormProductsWithImportsAction {
  type: "SET_REMOTE_FORM_PRODUCTS_WITH_IMPORTS";
  payload: {
    remoteFormProductsWithImports: FormProduct[];
  };
}

export interface SetFilteredRemoteProductsAction {
  type: "SET_FILTERED_REMOTE_PRODUCTS";
  payload: {
    filteredRemoteProducts: Product[];
  };
}

export interface SetServiceProductsAction {
  type: "SET_SERVICE_PRODUCTS";
  payload: {
    serviceProducts: Product[];
  };
}

export interface SetConnectionsAction {
  type: "SET_CONNECTIONS";
  payload: {
    connections: ProductConnection[];
  };
}

export interface SetFilteredConnectionsAction {
  type: "SET_FILTERED_CONNECTIONS";
  payload: {
    filteredConnections: ProductConnection[];
  };
}

export interface SetLocalParentChildProductsAction {
  type: "SET_LOCAL_PARENT_CHILD_PRODUCTS";
  payload: {
    localParentChildProducts: Product[];
  };
}

export type SetInitializedAction = {
  type: "SET_INITIALIZED";
  payload: {
    initialized: boolean;
  };
};

export type SetErrorAction = {
  type: "SET_ERROR";
  payload: {
    errorKey: ErrorKey;
    error: Error | undefined;
  };
};

export type SetLoadingAction = {
  type: "SET_LOADING";
  payload: {
    loadingKey: LoadingKey;
    loading: boolean;
  };
};

export interface SetApprovalRequestsAction {
  type: "SET_APPROVAL_REQUESTS";
  payload: {
    approvalRequests: ApprovalStatusResponseRecord[];
  };
}

export type EnableVendorScopedDraftsAction = {
  type: "ENABLE_VENDOR_SCOPED_DRAFTS";
};

export type ProductsActions =
  | SetConnectionsAction
  | SetErrorAction
  | SetRemoteFormProductsAction
  | SetInitializedAction
  | SetLoadingAction
  | SetLocalProductsAction
  | SetRemoteProductsAction
  | SetServiceProductsAction
  | SetFilteredConnectionsAction
  | SetFilteredLocalProductsAction
  | SetFilteredRemoteProductsAction
  | SetSkuImportDraftsAction
  | PostSkuImportDraftAction
  | EditSkuImportDraftAction
  | SetRemoteFormProductsWithImportsAction
  | SetLocalParentChildProductsAction
  | SetApprovalRequestsAction
  | EnableVendorScopedDraftsAction;

type ProductsDispatch = React.Dispatch<ProductsActions>;
